/**
 * THE ROUTES, WRITTEN DOWN WHERE NEITHER SIDE OWNS THEM.
 *
 * A URL is a fact two programs have to agree about, and the ordinary way to hold it is for
 * the server to declare `@Get('verses/chunks/:chunk')` and the client to build
 * `` `/verses/chunks/${n}` `` — the same fact, spelled twice, in two languages, in two
 * repositories' worth of distance. This project has been bitten by that shape three times
 * in its own data (see `schema/derive.ts`), so the routes go where the derivations went.
 *
 * Each entry carries BOTH spellings deliberately:
 *
 *   pattern  what the server declares — with `:params`, as Nest and Express read it
 *   path     what a client asks for — with the arguments filled in
 *
 * They are not derivable from one another in a way TypeScript can check (the pattern is a
 * string, the builder is a function with typed arguments), so `api.test.ts` asserts every
 * builder's output actually matches its own pattern. That test is the join: a route renamed
 * on one side and not the other fails there rather than in a browser.
 */

export const API_PREFIX = 'api/v1';

/**
 * WHICH BUILD A CLIENT IS ASKING ABOUT.
 *
 * A range of verse text is 1.2 MB and never changes within a build, so a client should keep
 * it — and `verses/chunks/3` is the same URL in every build there has ever been, so keeping
 * it is exactly how a reader ends up holding text from a library that no longer exists.
 *
 * So the build's identity goes in the query: `verses/chunks/3?v=54a3f9db2afb258c`. A new
 * build is then a new URL, the old answer is never consulted again, and the server can
 * honestly say `immutable` about the one it gives back. The value is `Bundle.meta.version`,
 * a hash of everything the build produced, which the client already has because the bundle
 * is the first thing it fetches.
 *
 * A request WITHOUT it is answered exactly the same way, and told to revalidate instead. The
 * parameter is a promise the caller makes about what they are holding, not a key.
 */
export const VERSION_PARAM = 'v';

/**
 * The route parameter the compilation routes name the build with — `compilation/:version/…`.
 *
 * A path segment rather than `?v=` because those routes REFUSE a mismatch (409) instead of softening
 * a cache header, and a compulsory thing belongs where it cannot be omitted. Named here beside
 * `VERSION_PARAM` so the two conventions are read together and nobody adds a third.
 */
export const VERSION_PARAM_PATH = 'version';

/** The header every response carries, so a client can learn the version without asking. */
export const VERSION_HEADER = 'x-library-version';

/**
 * One route: how the server declares it, and how a client asks for it.
 *
 * `method` is here because the table stopped being all-GET. A client that builds the right
 * URL and sends the wrong verb gets a 404 that reads exactly like a missing route, and the
 * hour spent on that is the reason the verb belongs beside the path rather than in a
 * sentence somewhere.
 */
export interface Route<Args extends readonly unknown[]> {
  method: 'GET' | 'POST';
  pattern: string;
  path: (...args: Args) => string;
}

export const ROUTES = {
  /** Whether the process is up and what it is serving. */
  health: { method: 'GET', pattern: 'health', path: () => 'health' } as Route<[]>,
  /**
   * THE ONE ROUTE A READER'S CLIENT MUST HIT, AND THE ONLY ONE THAT NEEDS A NETWORK.
   *
   * It answers "which build, and where is its content?" — a version and a base URL. The
   * client then fetches the bundle and the verse ranges as static files from that base, by
   * version (`<baseUrl>/<version>/…`), which is the Railway origin in development and a
   * Cloudflare edge in production. The heavy bytes never come through the server; this tiny
   * answer is the whole of the server's read path.
   *
   * It is also the seam offline hangs on: a client that has cached content at a version can
   * read entirely from its own store, and only this call needs the network — see
   * `server/DESIGN.md` and `ARCHITECTURE.md` D2.
   */
  manifest: { method: 'GET', pattern: 'manifest', path: () => 'manifest' } as Route<[]>,
  /** The map itself: names, stories, differences, references. No verse text. */
  bundle: { method: 'GET', pattern: 'bundle', path: () => 'bundle' } as Route<[]>,
  /** One range of verse text. Which range a passage is in comes from `Bundle.chunkStarts`. */
  verseChunk: {
    method: 'GET',
    pattern: 'verses/chunks/:chunk',
    path: (chunk: number) => `verses/chunks/${chunk}`,
  } as Route<[chunk: number]>,
  /** One text's search postings. */
  searchIndex: {
    method: 'GET',
    pattern: 'search/:corpus',
    path: (corpus: string) => `search/${encodeURIComponent(corpus)}`,
  } as Route<[corpus: string]>,
  /** One text's per-name verse lists. */
  nodeRefs: {
    method: 'GET',
    pattern: 'refs/:corpus',
    path: (corpus: string) => `refs/${encodeURIComponent(corpus)}`,
  } as Route<[corpus: string]>,
  /**
   * WHAT THIS SERVER WILL NOT SERVE, AND HOW MUCH IT HAS REFUSED.
   *
   * The build has always printed what it held back; a server has to be able to say it to a
   * reader, because on a server "held back" is a decision being taken continuously rather
   * than once. The counts are the part that matters: a gate that has refused nothing since
   * the process started is either serving a fully-cleared library or not running at all,
   * and those look identical from outside.
   */
  heldBack: { method: 'GET', pattern: 'library/held-back', path: () => 'library/held-back' } as Route<[]>,

  /*
   * ═════════════════════════════════════════════════════════════════════════════════════════
   * THE COMPILATION — `ARCHITECTURE.md D26`, designed in `DELIVERY.md §4` and §5.
   *
   * Everything above is the LIBRARY: scripture and its apparatus, public, served from the edge,
   * heavy. These are the COMPILATION: what this project made. They are answered by the server so
   * that taking it is costly and attributable rather than free and anonymous, and they are answered
   * PER VIEW rather than in bulk — one request for the whole layer is 348 kB gzipped today and 6.4
   * MB by twenty thousand figures (measured), and it would leave a coverage limit nothing to see.
   *
   * THE VERSION IS IN THE PATH, not in `?v=` as the library routes have it. Two reasons, and the
   * second is the load-bearing one. A path cannot be forgotten — `core/content.ts` puts the version
   * in every content URL for exactly that reason. And a caller naming a build this server does not
   * have must get a **409**, not the current build's bytes: a client mid-session when a new library
   * publishes would otherwise be handed a graph that disagrees with the nodes it already holds, and
   * would draw it without noticing. A cache-header downgrade cannot express that; a route can.
   * ═════════════════════════════════════════════════════════════════════════════════════════
   */

  /** The zoomed-out view: one glyph per screen cell, bounded by the screen rather than the corpus. */
  compilationOverview: {
    method: 'GET',
    pattern: 'compilation/:version/map/overview',
    path: (version: string) => `compilation/${encodeURIComponent(version)}/map/overview`,
  } as Route<[version: string]>,

  /** Every figure the map draws, as rows, with its edges as indices into them. */
  compilationSkeleton: {
    method: 'GET',
    pattern: 'compilation/:version/map/skeleton',
    path: (version: string) => `compilation/${encodeURIComponent(version)}/map/skeleton`,
  } as Route<[version: string]>,

  /**
   * WHERE EVERY TILE IS AND HOW MANY FIGURES IT HOLDS — the first request the streamed map makes.
   *
   * Fetched once and then never again: it is what tells the renderer which tiles to ask for as the
   * camera moves, and what lets it draw a region nobody has fetched as a cloud of about the right
   * brightness rather than as a hole.
   */
  compilationTiles: {
    method: 'GET',
    pattern: 'compilation/:version/map/tiles',
    path: (version: string) => `compilation/${encodeURIComponent(version)}/map/tiles`,
  } as Route<[version: string]>,

  /**
   * ONE TILE OF THE WORLD.
   *
   * The id is `<X>_<Y>` and is built from the CAMERA rather than from anything a reader typed — but
   * it still reaches a path, so it is encoded like every other segment here.
   */
  compilationTile: {
    method: 'GET',
    pattern: 'compilation/:version/map/tile/:id',
    path: (version: string, id: string) =>
      `compilation/${encodeURIComponent(version)}/map/tile/${encodeURIComponent(id)}`,
  } as Route<[version: string, id: string]>,

  /** What one click needs: the half of a gazetteer record the map never draws, and its ties. */
  compilationNode: {
    method: 'GET',
    pattern: 'compilation/:version/node/:id',
    path: (version: string, id: string) =>
      `compilation/${encodeURIComponent(version)}/node/${encodeURIComponent(id)}`,
  } as Route<[version: string, id: string]>,

  /**
   * MANY RECORDS IN ONE REQUEST — the prefetch, and not an optimisation.
   *
   * `DELIVERY.md §4.8` measured a click as costing one round trip and almost no bytes, so the fix is
   * to have the record before the click rather than to make it smaller. Fetched one at a time that
   * is ruinous: 35 records cost 6× on 4G and 200 cost 27×, against the browser's concurrency cap of
   * six. This route is what makes any prefetch policy affordable at all.
   *
   * A GET, WITH THE IDS IN THE QUERY, and the first draft of this had it as a POST. Two reasons it is
   * not, and the second is the one that matters:
   *
   *  1. Every route here is a read (see the note below), and a POST is how a write path arrives one
   *     convenience at a time. The test for that invariant is what caught it.
   *  2. **A browser does not cache a POST.** §9 gives the compilation `private, immutable` precisely so
   *     that a returning reader re-fetches nothing — and a prefetch whose answers cannot be cached is a
   *     prefetch that has to happen again on the next visit, which is the opposite of the point.
   *
   * `ids` and `reason` travel as query parameters, appended by the caller rather than built here — the
   * same convention `VERSION_PARAM` already follows, and the reason the path builder takes neither.
   */
  compilationNodes: {
    method: 'GET',
    pattern: 'compilation/:version/nodes',
    path: (version: string) => `compilation/${encodeURIComponent(version)}/nodes`,
  } as Route<[version: string]>,
} as const;

/** The query parameter carrying the ids a prefetch wants — comma-separated, capped by the server. */
export const IDS_PARAM = 'ids';

/**
 * The query parameter saying who asked.
 *
 * `reader` — the caller asked for these by name. `prefetch` — a policy chose them. Not decoration: §8
 * limits by how much of the compilation a caller has covered, because extraction is breadth rather
 * than rate, and §4.8's prefetch hands a reader a quarter of the layer in three clicks without their
 * asking. A limit that cannot tell those apart either throttles the curious or funds the scraper.
 *
 * It is also part of the URL, which means the two kinds of fetch are separate cache entries. That is
 * correct rather than wasteful: they are the same bytes, and they are counted differently.
 */
export const REASON_PARAM = 'reason';

/** What it answers: the records that exist, keyed by id. A missing id is absent, not null. */
export interface NodeRecordsResponse {
  records: Record<string, { node: Record<string, unknown>; ties: (readonly [string, number] | readonly [string, number, string])[] }>;
  /** Ids that were asked for and do not exist in this build — said plainly rather than implied. */
  missing: string[];
}

/*
 * NO WRITE ROUTES, ON PURPOSE (D1-B / D4).
 *
 * There was a `POST /admin/reload` with an admin key. Under D1-B the server holds no library —
 * content is published to R2 and served from Cloudflare's edge — so there is nothing to reload
 * from disk: a new library is published and the version pointer flips, which `/manifest` reads.
 * The reload endpoint, its key and its header are gone, and with them the only authenticated
 * write path. Every route here is a read.
 */

/**
 * What `GET /manifest` answers.
 *
 * Deliberately small — it is fetched on every visit and is the one thing that changes when a
 * new library is published. `langs` and `capabilities` are not here because they are not
 * built yet; they arrive with the multilingual split and the runtime features that need
 * them, not as empty placeholders that invite a client to read a promise nothing keeps.
 */
export interface ManifestResponse {
  /** The build the client should read — `Bundle.meta.version`, a content hash. */
  version: string;
  content: {
    /**
     * Where versioned content lives. A client builds `<baseUrl>/<version>/<file>`.
     *
     * `''` means same origin — development, where the server itself serves the content. A
     * Cloudflare domain in production. The client never hard-codes this; it is told, so it
     * can be moved to the edge with no client change.
     */
    baseUrl: string;
  };
  /** Where this build came from — provenance, echoed from `Bundle.meta.built`. */
  built: string;
  /**
   * WHETHER THIS DEPLOYMENT HAS AN IDENTITY PLANE AT ALL.
   *
   * A content-only deploy — no `VV_MONGO_URI`, which is every deploy so far and a deliberate state
   * rather than a broken one — loads no `SocialModule`, so `auth/me`, the discussion routes and the
   * rooms do not exist. The client had no way to know that and asked anyway: one doomed request per
   * page load, refused by the browser before the app ever saw it, and a CORS error in the console
   * that no `catch` can suppress because the browser writes it, not the promise.
   *
   * Told rather than inferred, for the same reason `content.baseUrl` is: the client should not have
   * to learn the shape of a deployment by failing against it.
   *
   * OPTIONAL, AND ITS ABSENCE MEANS "ASK ANYWAY". A client newer than the server it is talking to
   * sees nothing here; the safe reading is the old behaviour — try, and treat a refusal as signed
   * out — rather than silently deciding a working plane is absent.
   */
  social?: {
    enabled: boolean;
  };
}

/** What `GET /health` answers. */
export interface HealthResponse {
  status: 'ok';
  /** Where the bundle this process is serving came from — provenance, not a time. */
  built: string;
  /** The identity of this build, as `Bundle.meta.version` gives it. */
  version: string;
  /**
   * Passages published, per corpus id — and how many ranges exist.
   *
   * Present only when the process holds a local library (development). In production (D1-B) the
   * server holds no library — content is on the edge — so it reports the version it points at
   * and omits the counts, which are a property of the content it no longer serves.
   */
  counts?: Record<string, number>;
  chunkCount?: number;
  /**
   * WHERE THE COMPILATION IS BEING READ FROM — a bucket, or a directory (D26, `DELIVERY.md §11`).
   *
   * Not decoration, and the same argument as `heldBack`'s counts. A deployment reading the compilation
   * from the object store and one reading it from a local `artifacts/` directory look identical from
   * outside, and in production only one of those is correct — the whole of D26 in a deployed process is
   * that this plane is answered by the server rather than handed to the edge. Stated plainly so a
   * misconfiguration is visible on the health route rather than discovered as an empty map.
   */
  compilation?: string;
}

/**
 * HOW `HealthResponse.compilation` SAYS "through the public door" — spelled once.
 *
 * The server writes that line (`PublicObjectReader.describe()`) and the publish reads it, and those
 * are two programs on two machines agreeing about a string. This repository's most expensive
 * recurring fault is a wire format spelled in two codebases and shared by neither — the `?compare=`
 * that was one prefix short on all 36 episode pages — so the phrase lives here and both ends import
 * it.
 *
 * It matters because the two settings it joins must agree: a server on the public door cannot
 * survive the bucket prefix being closed (`DELIVERY.md §12` step 5), and a signed one survives
 * either. Read `readsPublicDoor` for the question actually being asked.
 */
export const PUBLIC_DOOR = '(public content base)';

/**
 * Whether a health response says the compilation is read through the public door.
 *
 * `undefined` where the route did not say — an older build, or a deployment holding a local
 * `artifacts/` directory, neither of which is the state this asks about. Absence is not "no": a
 * caller that treated it as such would report a closed door on a server that never mentioned one.
 */
export function readsPublicDoor(health: Pick<HealthResponse, 'compilation'>): boolean | undefined {
  const said = health.compilation;
  if (said === undefined || said === '') return undefined;
  return said.includes(PUBLIC_DOOR);
}

/** One edition this server declines to publish, and what it is waiting for. */
export interface HeldBackEdition {
  edition: string;
  licence: string;
  awaiting?: string;
}

/** What `GET /library/held-back` answers. */
export interface HeldBackResponse {
  editions: HeldBackEdition[];
  /**
   * What the gate has actually removed since this process started — not what it would.
   *
   * `passages` counts translations withheld outright; `originals` counts passages that
   * shipped without their Hebrew, Greek, Aramaic or Arabic because the grant covering the
   * original had not arrived; `alternates` counts passages that shipped without their
   * second English. Three numbers because they are three permissions, and a total would
   * hide which one had been refused.
   */
  refused: { passages: number; originals: number; alternates: number };
}

/**
 * A full URL for a route path.
 *
 * `base` may or may not end in a slash and may or may not already carry the prefix; both
 * were being handed in during the frontend's migration and both are ordinary. The prefix is
 * added exactly once.
 */
export function apiUrl(base: string, path: string): string {
  const root = base.replace(/\/+$/, '');
  const prefixed = root.endsWith(`/${API_PREFIX}`) ? root : `${root}/${API_PREFIX}`;
  return `${prefixed}/${path.replace(/^\/+/, '')}`;
}

/**
 * Where a versioned content file lives: `<baseUrl>/<version>/<file>`.
 *
 * `file` is an artifact's JSON path (`ARTIFACTS.*.json` from `./artifacts`), so the client,
 * the server's dev static mount, and the R2 publish step all name a file the same way and
 * cannot drift. Unlike `apiUrl` this adds NO API prefix: content is static files, not routes.
 *
 * `baseUrl` `''` yields an absolute-from-root path (`/<version>/<file>`), which is the
 * same-origin case; a real base yields an absolute URL. The version sits in the PATH, not a
 * query, so each build is a distinct immutable URL a CDN and a service worker can keep for
 * ever — see `VERSION_PARAM`'s note for why the path and not `?v=`.
 */
export function contentUrl(baseUrl: string, version: string, file: string): string {
  const root = baseUrl.replace(/\/+$/, '');
  return `${root}/${contentKey(version, file)}`;
}

/**
 * The storage key an artifact is published under: `<version>/<file>`.
 *
 * This is the same suffix `contentUrl` builds, without a base — the R2 object key. The two
 * share `contentKey` on purpose: the key the pipeline uploads to and the URL the client
 * fetches from are one string with the bucket's public base in front, so they cannot drift
 * (the defect this package exists to end). No leading slash — an object key is not a path.
 */
export function contentKey(version: string, file: string): string {
  return `${encodeURIComponent(version)}/${file.replace(/^\/+/, '')}`;
}

/**
 * THE ONE MUTABLE OBJECT: the pointer to the live build.
 *
 * The pipeline writes it after uploading a version's content; the server reads it (short-
 * cached) to answer the manifest. Its own key is fixed, so the server always knows where to
 * look, and it is the only object in the bucket that is ever overwritten — everything under a
 * `<version>/` prefix is immutable.
 */
export const POINTER_FILE = 'latest.json';

/** What `latest.json` holds. */
export interface LibraryPointer {
  version: string;
  /** Provenance, echoed so the manifest can report it without fetching the bundle. */
  built: string;
}
